import { useEffect, useRef, useCallback } from 'react';

/**
 * Handle mobile back button for closing views/dialogs
 * When a view opens, pushes a history entry. When back button is pressed,
 * closes the view instead of navigating away.
 *
 * @param {boolean} isOpen - Whether the view is open
 * @param {Function} onClose - Function to close the view
 * @returns {Function} prepareNavigate - Call before navigate() to prevent interference
 */
export function useBackButton(isOpen, onClose) {
  const historyPushedRef = useRef(false);
  const wasOpenRef = useRef(isOpen);
  const onCloseRef = useRef(onClose);
  const navigatingRef = useRef(false);

  // Keep onClose ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // Opening: push history entry
    if (isOpen && !wasOpenRef.current) {
      window.history.pushState({ backButton: true }, '');
      historyPushedRef.current = true;
      navigatingRef.current = false;
    }

    // Closing programmatically (not via back button): remove history entry
    // Skip if we're navigating away — history.back() would undo the navigation
    if (!isOpen && wasOpenRef.current && historyPushedRef.current) {
      if (!navigatingRef.current) {
        window.history.back();
      }
      historyPushedRef.current = false;
    }

    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = () => {
      if (historyPushedRef.current && !navigatingRef.current) {
        onCloseRef.current();
        historyPushedRef.current = false;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen]);

  // Cleanup on unmount — do NOT call history.back() here.
  // The extra history entry is harmless — it just means one extra "back" press on the same page.
  useEffect(() => {
    return () => {
      historyPushedRef.current = false;
    };
  }, []);

  // Call this before navigate() to prevent the hook from calling history.back()
  // which would undo the navigation. The extra history entry stays but is harmless.
  const prepareNavigate = useCallback(() => {
    navigatingRef.current = true;
    historyPushedRef.current = false;
  }, []);

  return prepareNavigate;
}
